import React, { useState, useEffect } from 'react';
import { useToast } from "@/hooks/use-toast";
import { useAuth } from '@/contexts/auth/AuthContext';
import { ProfileUpdateData, PersonName, NotificationPreferences as NotificationPrefsType } from '@/contexts/auth/types';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import ProfileHeader from '@/components/profile/ProfileHeader';
import ProfileTabs, { ProfileTab } from '@/components/profile/ProfileTabs';
import ParticipantProfileForm from '@/components/profile/ParticipantProfileForm';
import { User, Bell } from "lucide-react";
import { useLocation } from 'react-router-dom';
import NotificationPreferences from '@/components/notification/NotificationPreferences';

const ParticipantProfile = () => {
  const { user, updateProfile, isLoading } = useAuth();
  const { toast } = useToast();
  const location = useLocation();
  const locationState = location.state as { defaultTab?: string; _ts?: number } | null;
  const [activeTab, setActiveTab] = useState<string>(locationState?.defaultTab || 'profil');

  const [tabInitialized, setTabInitialized] = useState(false);

  useEffect(() => {
    if (!tabInitialized && locationState?.defaultTab) {
      setActiveTab(locationState.defaultTab);
      setTabInitialized(true);
      window.history.replaceState({}, document.title); // clears location.state
    }
  }, [tabInitialized, locationState?.defaultTab]);

  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    academicTitle: '',
    formalSalutation: '',
  });

  useEffect(() => {
    if (user) {
      setFormData({
        firstName: user.userProfile.name.firstName || '',
        lastName: user.userProfile.name.lastName || '',
        academicTitle: user.userProfile.name.academicTitle || '',
        formalSalutation: user.userProfile.name.formalSalutation || '',
      });
    }
  }, [user]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleProfileUpdate = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!user) return;

    const name: PersonName = {
      firstName: formData.firstName,
      lastName: formData.lastName,
      academicTitle: formData.academicTitle || undefined,
      formalSalutation: formData.formalSalutation || undefined
    };

    const updateData: ProfileUpdateData = {
      owner: 'me',
      name,
      notificationPreferences: user.userProfile.notificationPreferences
    };

    const success = await updateProfile(updateData);

    if (success) {
      toast({
        title: "Profil frissítve",
        description: "Az Ön profiladatai sikeresen frissítve lettek.",
        variant: "default",
      });
    }
  };

  const handleNotificationPreferencesChange = async (preferences: NotificationPrefsType) => {
    if (!user) return;

    const name: PersonName = {
      firstName: formData.firstName,
      lastName: formData.lastName,
      academicTitle: formData.academicTitle || undefined,
      formalSalutation: formData.formalSalutation || undefined
    };

    const updateData: ProfileUpdateData = {
      owner: 'me',
      name,
      notificationPreferences: preferences
    };

    const success = await updateProfile(updateData);

    if (success) {
      toast({
        title: "Értesítési beállítások frissítve",
        description: "Az Ön értesítési beállításai sikeresen frissítve lettek.",
        variant: "default",
      });
    }
  };

  if (!user) {
    return null;
  }

  const profileTabs: ProfileTab[] = [
    {
      id: "profil",
      label: (
        <>
          <User size={16} />
          Profil adatok
        </>
      ),
      content: (
        <ParticipantProfileForm
          formData={formData}
          isLoading={isLoading}
          handleInputChange={handleInputChange}
          handleProfileUpdate={handleProfileUpdate}
        />
      )
    },
    {
      id: "ertesitesek",
      label: (
        <>
          <Bell size={16} />
          Értesítési beállítások
        </>
      ),
      content: (
        user.userProfile.notificationPreferences && (
          <NotificationPreferences
            preferences={user.userProfile.notificationPreferences}
            onChange={handleNotificationPreferencesChange}
          />
        )
      )
    }
  ];

  return (
    <div className="flex min-h-screen flex-col">
      <Navbar />
      <div className="flex-1 mt-16">
        <div className="container max-w-screen-lg py-12">
          <ProfileHeader
            title="Felhasználói profil"
            description="Kezelje adatait és értesítési beállításait"
          />

          <ProfileTabs
            tabs={profileTabs}
            activeTab={activeTab}
            onTabChange={setActiveTab}
          />
        </div>
      </div>
    </div>
  );
};

export default ParticipantProfile;
